/* eslint-disable prefer-arrow/prefer-arrow-functions */
import React, { useState, useRef } from 'react'

import clsx from 'clsx'

import Tooltip from 'components/Tooltip'
import { useEventContext } from 'context/EventContext'
import { thousandSeparator } from 'helpers/formatters'
import useViewport from 'hooks/useViewport'

import ChartContainer from '../../UsersAndUseCases/components/ChartContainer'

import classes from './DiamondsChart.module.scss'

const AREA_PROPORTION = 0.5 // Adjusted area proportion for better visibility
const SQUARE_COLOR_CLASS_PREFIX = 'square-color-'
const TOTAL_SQUARE_AREA = 100 // Total area for the chart, used for scaling
const EMPTY_STATE_DATA = [
  { name: '-', slug: 'empty-1', percentage: 5 },
  { name: '-', slug: 'empty-2', percentage: 45 },
  { name: '-', slug: 'empty-3', percentage: 25 },
  { name: '-', slug: 'empty-4', percentage: 15 },
  { name: '-', slug: 'empty-5', percentage: 10 },
]
const GRAY_COLOR_CLASS_PREFIX = 'square-gray-'

/**
 * Formats API data to the format expected by DiamondsChart.
 * @param {Object} data - API data object with use case names as keys and counts as values, including a "Total" key.
 * @returns {Array<{name: string, slug: string, percentage: number}>}
 */

export function formatDiamondsChartData(data) {
  // eslint-disable-next-line no-unused-vars
  const { Total, ...itemsParam } = data
  if (!data || typeof data !== 'object' || !data.Total) return []

  const total = Object.values(itemsParam).reduce((sum, count) => sum + count, 0)
  const items = Object.entries(itemsParam).map(([name, count]) => ({
    name,
    slug: name.toLowerCase().replace(/\s+/g, '-'),
    percentage: Math.round((count / total) * 100),
    value: count,
  }))
  if (items.length < 2) return items

  // Find smallest and largest
  const sorted = [...items].sort((a, b) => a.percentage - b.percentage)
  const smallest = sorted[0]
  const largest = sorted[sorted.length - 1]
  // Remove smallest and largest from items
  const rest = items.filter(i => i !== smallest && i !== largest)
  // Sort rest biggest to smallest
  const restSorted = rest.sort((a, b) => b.percentage - a.percentage)
  // Return: [smallest, largest, ...restSorted]
  return [smallest, largest, ...restSorted]
}

/**
 * Treemap component using squarified algorithm for better aspect ratios
 * @param {Array} data - Array of items with name and percentage properties
 * @param {string} title - Chart title
 */
const DiamondsChart = ({ data = [], title = 'Use cases' }) => {
  const [hovered, setHovered] = useState(null)
  const { up } = useViewport()
  const { sendEvent } = useEventContext({ name: 'engagement_hover' })
  const isDesktop = up('lg')
  const tooltipOpenRefs = useRef({}) // Store setOpen functions for each square

  const isEmpty =
    !data ||
    (Array.isArray(data) && data.length === 0) ||
    (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0)
  const chartData = isEmpty ? EMPTY_STATE_DATA : formatDiamondsChartData(data)

  const rankedByPercentage = [...chartData].sort((a, b) => b.percentage - a.percentage)

  const chartSquares = chartData.map(item => {
    const rank = rankedByPercentage.map(element => element.slug).indexOf(item.slug)

    const area = TOTAL_SQUARE_AREA * item.percentage * AREA_PROPORTION
    const side = Math.sqrt(area)
    return { ...item, side, rank }
  })

  const rows = [[], []]
  chartSquares.forEach((sq, i) => rows[i % 2].push(sq))

  const handleHover = slug => {
    if (isEmpty) {
      return
    }

    setHovered(slug)

    sendEvent({
      engagementComponent: 'product_users-and-use-cases',
      engagementDestination: 'show_use_case_details',
      engagementDetails: 'hover_use-cases-graph',
      name: 'engagement_hover',
    })
  }

  const renderSquares = row =>
    row.map(sq => {
      const squareDiv = (
        <div
          className={clsx(
            classes.square,
            isEmpty
              ? classes[`${GRAY_COLOR_CLASS_PREFIX}${sq.rank}`]
              : classes[`${SQUARE_COLOR_CLASS_PREFIX}${sq.rank}`],
            {
              [classes.empty]: isEmpty,
              [classes.hovered]: hovered === sq.slug,
              [classes.dimmed]: hovered && hovered !== sq.slug,
            }
          )}
          role="button"
          style={{
            width: `${sq.side}px`,
            height: `${sq.side}px`,
          }}
          onMouseEnter={() => {
            handleHover(sq.slug)
          }}
        />
      )
      if (isEmpty) {
        return <React.Fragment key={sq.slug}>{squareDiv}</React.Fragment>
      }
      return (
        <Tooltip
          key={sq.slug}
          title={
            <span>
              {sq.percentage}% ({thousandSeparator(sq.value)} reviews)
            </span>
          }
          placement="top"
          onTooltipClose={() => setHovered(null)}
          setOpen={setOpen => {
            tooltipOpenRefs.current[sq.slug] = setOpen
          }}
        >
          {squareDiv}
        </Tooltip>
      )
    })

  const renderLegend = () =>
    rankedByPercentage.map((item, index) => (
      <div
        key={item.slug}
        className={clsx(classes.legendItem, {
          [classes.dimmed]: hovered && hovered !== item.slug,
          [classes.empty]: isEmpty,
        })}
        onMouseEnter={() => {
          handleHover(item.slug)
          // Open tooltip for corresponding square
          if (!isEmpty && tooltipOpenRefs.current[item.slug]) {
            tooltipOpenRefs.current[item.slug](true)
          }
        }}
        onMouseLeave={() => {
          setHovered(null)
          // Close tooltip for corresponding square
          if (!isEmpty && tooltipOpenRefs.current[item.slug]) {
            tooltipOpenRefs.current[item.slug](false)
            // Clean up after hover
            delete tooltipOpenRefs.current[item.slug]
          }
        }}
        role="button"
      >
        <div
          className={clsx(
            classes.colorBox,
            isEmpty ? classes[`${GRAY_COLOR_CLASS_PREFIX}${index}`] : classes[`${SQUARE_COLOR_CLASS_PREFIX}${index}`]
          )}
        />
        <span className={clsx(classes.legendText, { [classes.hovered]: hovered === item.slug })}>{item.name}</span>
      </div>
    ))

  return (
    <ChartContainer
      title={title}
      className={clsx(classes.chartContainer, {
        [classes.isEmpty]: isEmpty,
      })}
      divider={isDesktop ? 'left' : 'top'}
      isEmpty={isEmpty}
    >
      <div className={classes.divider} />
      <div className={classes.container}>
        <div>
          <div className={classes.chart}>
            {rows.map((row, index) => (
              <div className={classes.row} key={`row-${index}`}>
                {renderSquares(row)}
              </div>
            ))}
          </div>
        </div>
        <div className={classes.legendContainer}>{renderLegend()}</div>
      </div>
    </ChartContainer>
  )
}

export default DiamondsChart
